import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import {
  all,
  fork,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { Pages } from '../../constants/pages';
import { SORTING_KEYS } from '../../constants/sortingKeys';
import { COMPONENT_REDUCER } from '../../constants/store';
import { defaultPageFilters } from '../../utilities/defaultPageFilters';
import {
  filterAndSortingKeys,
  noSelectionKey,
} from '../../utilities/filterAndSortingsUtil';
import { getPaginationFilters } from '../../utilities/paginationFilters';
import { getPaginationSorting } from '../../utilities/paginationSortings';
import { getPaginatedHearingsInitialize } from '../actions/api';
import { cityAreaByIdSelector } from '../apiFeatures/cityAreaSlice';
import { hearingStatusByIdSelector } from '../apiFeatures/hearingStatusSlice';
import { hearingTypeByIdSelector } from '../apiFeatures/hearingTypeSlice';
import { subjectAreaByIdSelector } from '../apiFeatures/subjectAreaSlice';
import { shouldPaginateHearingsSelector } from '../bootstrapSlice';
import { parseApiError } from '../globalMessageSlice';
import { getAttr, getEntityId } from '../helpers/apiHelpers';
import { callAndAwaitApi } from '../helpers/sagaHelpers';
import { getRequestData, getRequestMetaData } from '../helpers/selectorHelpers';
import { defaultPageIncludes } from '../../constants/defaultPageIncludes';

// ---------- ACTIONS -----------
const setCurrentHearingsPage = createAction(
  'component/hearingOverview/setCurrentPage',
);
const setTotalHearingsPages = createAction(
  'component/hearingOverview/setTotalPages',
);
const setFilterAndSortingSelection = createAction(
  'component/hearingOverview/setFilterAndSortingSelection',
);
const setTitleFilter = createAction('component/hearingOverview/setTitleFilter');
const setActivePage = createAction('component/hearingOverview/setActivePage');
const updateActivePage = createAction(
  'component/hearingOverview/updateActivePage',
);
const fetchHearingsForPage = createAction(
  'component/hearingOverview/fetchHearingsForPage',
);
const setPaginatedHearings = createAction(
  'component/hearingOverview/setPaginatedHearings',
);
const updateCurrentPage = createAction(
  'component/hearingOverview/updateCurrentPage',
);
const updateFilterAndSortingSelection = createAction(
  'component/hearingOverview/updateFilterAndSortingSelection',
);
const resetPaginatedHearings = createAction(
  'component/hearingOverview/resetPaginatedHearings',
);

const hearingsPerPage = process.env.REACT_APP_HEARINGS_PER_PAGE;

const initialPageState = (pageFilters, includes) => {
  return {
    currentPage: 1,
    totalPages: 1,
    includes: includes,
    titleFilter: null,
    pageFilters: pageFilters,
    filterAndSortingSelection: {
      [filterAndSortingKeys.sortingKey]: SORTING_KEYS.DEADLINE_SOONEST,
      [filterAndSortingKeys.subjectArea]: noSelectionKey,
      [filterAndSortingKeys.cityArea]: noSelectionKey,
      [filterAndSortingKeys.hearingType]: noSelectionKey,
      [filterAndSortingKeys.hearingStatus]: noSelectionKey,
      [filterAndSortingKeys.hearingOwner]: noSelectionKey,
      [filterAndSortingKeys.title]: noSelectionKey,
    },
    paginatedHearings: {},
  };
};

const initialState = {
  activePage: Pages.CurrentHearingsPage,
  pageSize: hearingsPerPage === undefined ? 12 : parseInt(hearingsPerPage),
  pageStates: {
    [Pages.CurrentHearingsPage]: initialPageState(
      defaultPageFilters.CurrentHearingsPage,
      defaultPageIncludes.CurrentHearingsPage.hearing,
    ),
    [Pages.ConcludedHearingsPage]: initialPageState(
      defaultPageFilters.ConcludedHearingsPage,
      defaultPageIncludes.ConcludedHearingsPage.hearing,
    ),
    [Pages.AdministrationPage]: initialPageState(
      defaultPageFilters.AdministrationPage,
      defaultPageIncludes.AdministrationPage.hearing,
    ),
    [Pages.MyHearingsPageWritten]: initialPageState(
      defaultPageFilters.MyHearingsPageWritten,
      defaultPageIncludes.MyHearingsPageWritten.hearing,
    ),
    [Pages.MyHearingsPageReviewed]: initialPageState(
      defaultPageFilters.MyHearingsPageReviewed,
      defaultPageIncludes.MyHearingsPageReviewed.hearing,
    ),
    [Pages.MyHearingsPageResponded]: initialPageState(
      defaultPageFilters.MyHearingsPageResponded,
      defaultPageIncludes.MyHearingsPageResponded.hearing,
    ),
    [Pages.MyHearingsPageInvited]: initialPageState(
      defaultPageFilters.MyHearingsPageInvited,
      defaultPageIncludes.MyHearingsPageInvited.hearing,
    ),
  },
};

// ---------- REDUCERS -----------
const hearingOverviewReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(setCurrentHearingsPage, (state, action) => {
      const activePage = state.activePage;
      state.pageStates[activePage].currentPage = action.payload;
    })
    .addCase(setTotalHearingsPages, (state, action) => {
      const activePage = state.activePage;
      state.pageStates[activePage].totalPages = action.payload;
    })
    .addCase(setTitleFilter, (state, action) => {
      const activePage = state.activePage;
      state.pageStates[activePage].titleFilter = action.payload;
    })
    .addCase(setFilterAndSortingSelection, (state, action) => {
      const activePage = state.activePage;
      state.pageStates[activePage].filterAndSortingSelection = action.payload;
    })
    .addCase(setActivePage, (state, action) => {
      state.activePage = action.payload;
    })
    .addCase(setPaginatedHearings, (state, action) => {
      const activePage = state.activePage;
      const pageIndex = action.payload.pageIndex;
      const hearingIds = action.payload.hearingIds;
      state.pageStates[activePage].paginatedHearings[pageIndex] = hearingIds;
    })
    .addCase(resetPaginatedHearings, (state) => {
      const activePage = state.activePage;
      state.pageStates[activePage].paginatedHearings = {};
    });
});

// --------- Sagas ----------
export function* hearingOverviewSaga() {
  yield all([
    fork(fetchHearingsForPageSaga),
    fork(updateActivePageSaga),
    fork(updateCurrentPageSaga),
    fork(updateFilterAndSortingSelectionSaga),
  ]);
}

function* updateFilterAndSortingSelectionSaga() {
  yield takeEvery(updateFilterAndSortingSelection.type, function* (action) {
    yield put(resetPaginatedHearings());
    yield put(setFilterAndSortingSelection(action.payload));
    yield put(updateCurrentPage(1));
  });
}

function* updateCurrentPageSaga() {
  yield takeEvery(updateCurrentPage.type, function* (action) {
    yield put(setCurrentHearingsPage(action.payload));
    yield put(fetchHearingsForPage());
  });
}

function* updateActivePageSaga() {
  yield takeEvery(updateActivePage.type, function* (action) {
    yield put(setActivePage(action.payload));
    yield put(fetchHearingsForPage());
  });
}

function* fetchHearingsForPageSaga() {
  yield takeLatest(fetchHearingsForPage.type, function* () {
    const pageIndex = yield select(currentPageHearingsSelector);
    const pageSize = yield select(pageSizeHearingsSelector);
    const filterAndSortingSelection = yield select(
      filterAndSortingSelectionSelector,
    );
    const pageFilters = yield select(pageFilterSelector);
    const orderBy = yield select(paginationSortingSelector);
    const includes = yield select(pageIncludesSelector);

    // We map relevant filters, so we use the correct attribute rather than Entity-ID.
    const filterKeys = {};
    for (const [key, value] of Object.entries(filterAndSortingSelection)) {
      if (key === filterAndSortingKeys.sortingKey || value === noSelectionKey) {
        continue;
      }
      switch (key) {
        case filterAndSortingKeys.hearingStatus:
          const status = yield select(hearingStatusByIdSelector(value));
          filterKeys[key] = getAttr(status, 'status');
          break;
        case filterAndSortingKeys.hearingType:
          const type = yield select(hearingTypeByIdSelector(value));
          filterKeys[key] = getAttr(type, 'name');
          break;
        case filterAndSortingKeys.subjectArea:
          const subjectArea = yield select(subjectAreaByIdSelector(value));
          filterKeys[key] = getAttr(subjectArea, 'name');
          break;
        case filterAndSortingKeys.cityArea:
          const cityArea = yield select(cityAreaByIdSelector(value));
          filterKeys[key] = getAttr(cityArea, 'name');
          break;
        default:
          filterKeys[key] = value;
      }
    }

    const filters = getPaginationFilters(filterKeys, 'hearings', pageFilters);

    const actionResult = yield callAndAwaitApi(
      getPaginatedHearingsInitialize({
        includes,
        pageIndex,
        pageSize,
        filters,
        orderBy,
      }),
    );

    if (actionResult.success) {
      const meta = getRequestMetaData(actionResult.success);
      const totalPages = meta.Pagination.totalPages;
      const pageIndex = meta.Pagination.pageIndex;
      const hearings = getRequestData(actionResult.success);
      const hearingIds = hearings.map((h) => getEntityId(h));

      yield put(setTotalHearingsPages(totalPages));
      yield put(
        setPaginatedHearings({
          pageIndex: pageIndex,
          hearingIds: hearingIds,
        }),
      );
    } else {
      yield put(parseApiError(actionResult.error.payload));
    }
  });
}

// --------- Selectors ---------
const hearingOverviewState = (state) =>
  state.component[COMPONENT_REDUCER.HEARING_OVERVIEW];

const activePageSelector = createSelector(
  hearingOverviewState,
  (state) => state.activePage,
);

const pageStateSelector = createSelector(
  [hearingOverviewState, activePageSelector],
  (state, activePage) => state.pageStates[activePage],
);

const currentPageHearingsSelector = createSelector(
  pageStateSelector,
  (state) => state.currentPage,
);

const pageSizeHearingsSelector = createSelector(
  hearingOverviewState,
  (state) => state.pageSize,
);

const totalPagesHearingsSelector = createSelector(
  pageStateSelector,
  (state) => state.totalPages,
);

const filterAndSortingSelectionSelector = createSelector(
  pageStateSelector,
  (state) => state.filterAndSortingSelection,
);

const pageFilterSelector = createSelector(
  pageStateSelector,
  (state) => state.pageFilters,
);

const titleFilterSelector = createSelector(
  pageStateSelector,
  (state) => state.titleFilter,
);

const pageIncludesSelector = createSelector(
  pageStateSelector,
  (state) => state.includes,
);

const paginatedHearingsSelector = createSelector(
  [pageStateSelector, currentPageHearingsSelector],
  (state, currentPage) => state.paginatedHearings[currentPage] ?? [],
);

const paginationSortingSelector = createSelector(
  [filterAndSortingSelectionSelector],
  (filterAndSortingSelection) => {
    return getPaginationSorting(
      filterAndSortingSelection[filterAndSortingKeys.sortingKey],
      'hearings',
    );
  },
);

const areHearingsReadyForPageSelector = createSelector(
  [shouldPaginateHearingsSelector, paginatedHearingsSelector],
  (usingPaginatation, hearingIds) =>
    usingPaginatation && hearingIds !== undefined && hearingIds.length > 0,
);

export {
  activePageSelector,
  areHearingsReadyForPageSelector,
  currentPageHearingsSelector,
  fetchHearingsForPage,
  filterAndSortingSelectionSelector,
  pageSizeHearingsSelector,
  paginatedHearingsSelector,
  paginationSortingSelector,
  setActivePage,
  setCurrentHearingsPage,
  setFilterAndSortingSelection,
  setTitleFilter,
  setTotalHearingsPages,
  titleFilterSelector,
  totalPagesHearingsSelector,
  updateCurrentPage,
  updateFilterAndSortingSelection,
};

export default hearingOverviewReducer;
